import { useQuery } from '@tanstack/react-query';
import { Card, Col, Row, Space, Statistic, Tabs, Typography } from 'antd';
import {
  CrownOutlined,
  AppstoreOutlined,
  TagsOutlined,
  TeamOutlined,
} from '@ant-design/icons';
import { get } from '../../api/client';
import { LevelsTab } from './components/LevelsTab';
import { FeaturesManager } from './components/FeaturesManager';
import { FeatureMatrix } from './components/FeatureMatrix';
import { PlansTab } from './components/PlansTab';
import { SubscriptionsTab } from './components/SubscriptionsTab';
import type { LevelRow, PlanRow, FeatureRow, SubscriptionListResponse } from './types';

const TEAL = '#0d9488';

/** 顶部统计卡配置 */
const STAT_CARDS = [
  { key: 'levels', title: '会员等级', icon: <CrownOutlined />, color: '#0d9488' },
  { key: 'plans', title: '启用套餐', icon: <TagsOutlined />, color: '#6366f1' },
  { key: 'features', title: '功能权限项', icon: <AppstoreOutlined />, color: '#f59e0b' },
  { key: 'users', title: '订阅用户', icon: <TeamOutlined />, color: '#ec4899' },
] as const;

/**
 * 会员管理页（现代化重做，替代旧「会员权限」单表格页）。
 * 结构：顶部统计概览 + 四个标签页（会员等级 / 功能权限 / 会员套餐 / 订阅用户）。
 * 各标签页组件自取数据并共享 React Query 缓存键，统计卡复用同一批查询，避免重复请求。
 */
export default function MembershipPage() {
  const { data: levels } = useQuery({
    queryKey: ['membership-levels'],
    queryFn: () => get<LevelRow[]>('/admin/membership/levels'),
  });
  const { data: plans } = useQuery({
    queryKey: ['membership-plans'],
    queryFn: () => get<PlanRow[]>('/admin/membership/plans'),
  });
  const { data: features } = useQuery({
    queryKey: ['membership-features'],
    queryFn: () => get<FeatureRow[]>('/admin/membership/features'),
  });
  const { data: subs } = useQuery({
    queryKey: ['membership-subscriptions-count'],
    queryFn: () => get<SubscriptionListResponse>('/admin/membership/subscriptions', { page: 1, pageSize: 1 }),
  });

  const statValues: Record<string, number> = {
    levels: levels?.length ?? 0,
    plans: plans?.filter((p) => p.isActive).length ?? 0,
    features: features?.length ?? 0,
    users: subs?.total ?? 0,
  };

  return (
    <div>
      <div style={{ marginBottom: 20 }}>
        <Typography.Title level={4} style={{ marginBottom: 4 }}>
          会员管理
        </Typography.Title>
        <Typography.Text type="secondary">
          统一配置会员等级、功能权限、售卖套餐，并管理用户订阅状态。所有变更实时生效。
        </Typography.Text>
      </div>

      <Row gutter={[16, 16]} style={{ marginBottom: 20 }}>
        {STAT_CARDS.map((s) => (
          <Col key={s.key} xs={12} lg={6}>
            <Card styles={{ body: { padding: 20 } }}>
              <Space align="center" size={16}>
                <div
                  style={{
                    width: 48,
                    height: 48,
                    borderRadius: 14,
                    background: `${s.color}1a`,
                    color: s.color,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: 22,
                  }}
                >
                  {s.icon}
                </div>
                <Statistic title={s.title} value={statValues[s.key]} valueStyle={{ color: '#1f2329', fontWeight: 600 }} />
              </Space>
            </Card>
          </Col>
        ))}
      </Row>

      <Card styles={{ body: { paddingTop: 8 } }}>
        <Tabs
          defaultActiveKey="levels"
          items={[
            { key: 'levels', label: '会员等级', children: <LevelsTab /> },
            {
              key: 'features',
              label: '功能权限',
              children: (
                <div>
                  <FeaturesManager />
                  <FeatureMatrix />
                </div>
              ),
            },
            { key: 'plans', label: '会员套餐', children: <PlansTab /> },
            { key: 'subscriptions', label: '订阅用户', children: <SubscriptionsTab /> },
          ]}
        />
      </Card>

      <Typography.Text type="secondary" style={{ display: 'block', marginTop: 16, fontSize: 12, color: TEAL }}>
        说明：功能权限矩阵决定各等级可用能力；用户端权限由服务端下发，无需前端硬编码。
      </Typography.Text>
    </div>
  );
}
